import React, { useMemo } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import { useTranslation } from 'react-i18next'
import SocialButton from './SocialButton'
import Link from '../Link'
import { GatsbyImage } from 'gatsby-plugin-image'
import { Disclosure, Transition } from '@headlessui/react'
import { FaChevronDown } from 'react-icons/fa'
import { getPath } from '../../util/helper'
import cx from 'classnames'
import Logo from '../../assets/icons/logos/blok-logo.svg'
import LeadSELogo from '../../assets/icons/logos/vardera_logo_dark.svg'
import LeadNOLogo from '../../assets/icons/logos/hjemverdi_dark.svg'

const Footer = ({ footerConfig }) => {
  const { t, i18n } = useTranslation()

  const currentLocale = i18n.language
  const disableLogoLinks = footerConfig?.disableLogoLinks
  const linkTarget = disableLogoLinks ? '_blank' : '_self'

  const COUNTRY = process.env.GATSBY_COUNTRY

  const logo = {
    'LEAD_SE': <LeadSELogo className="w-40" />,
    'LEAD_NO': <LeadNOLogo className="w-40" />
  }[COUNTRY] || <Logo className="w-24" />

  const data = useStaticQuery(graphql`
    query FooterQuery {
      allContentfulFooter {
        edges {
          node {
            footerType
            country
            entryTitle
            email
            logo1Image {
              gatsbyImageData(layout: FIXED, width: 100, placeholder: BLURRED)
            }
            logo1Link
            logo2Image {
              gatsbyImageData(layout: FIXED, width: 100, placeholder: BLURRED)
            }
            logo2Link
            copyright
            streetAddress
            streetAddress2
            locality
            displayCountry
            vatId
            phone
            usernameFacebook
            usernameInstagram
            usernamePinterest
            usernameTwitter
            youtubeChannel
            termsOfService
            registry
            websiteLink
            websiteLinkText
            termsOfServiceTitle
            description {
              childMarkdownRemark {
                html
              }
            }
            privacyTitle
            aboutUsLink
            aboutUsTitle
            googleRatingNumber
            googleRatingText
            internalLinks {
              itemLabel
              itemLink
              id
              isDropdownType
              disabledLanguages
              dropdownItems {
                apartmentFiltersCity
                apartmentsFilterType
                isListingsItem
                itemLabel
                itemLink
                showDividerAfter
              }
            }
            node_locale
          }
        }
      }
    }
  `)

  const match = useMemo(
    () =>
      data.allContentfulFooter.edges.find(
        ({ node }) =>
          node.country === process.env.GATSBY_COUNTRY &&
          node.node_locale === currentLocale
      ),
    [data]
  )

  if (!match) {
    return null
  }

  const { node } = match

  return (
    <footer className="bg-white">
      <div className="container">
        <div className="flex flex-col justify-between py-12 border-t border-gray-400 md:items-center lg:py-14 md:flex-row">
          <div className="flex flex-col md:items-center md:flex-row">
            <ul className="flex space-x-2">
              {node.usernameFacebook ? (
                <SocialButton
                  site="facebook"
                  username={node.usernameFacebook}
                />
              ) : null}
              {node.usernameTwitter ? (
                <SocialButton site="twitter" username={node.usernameTwitter} />
              ) : null}
              {node.usernameInstagram ? (
                <SocialButton
                  site="instagram"
                  username={node.usernameInstagram}
                />
              ) : null}
              {node.usernamePinterest ? (
                <SocialButton
                  site="pinterest"
                  username={node.usernamePinterest}
                />
              ) : null}
              {node.youtubeChannel ? (
                <SocialButton site="youtube" username={node.youtubeChannel} />
              ) : null}
            </ul>
            <div className="mt-6 text-xl text-blue-500 font-bold md:mt-0 md:ml-6">
              {node.googleRatingNumber} {node.googleRatingText}
            </div>
          </div>
          <div className="flex space-x-10 mt-7 md:mt-0">
            {node.logo1Image ? (
              <a
                href={node.logo1Link}
                target="_blank"
                rel="noreferrer noopener"
              >
                <GatsbyImage image={node.logo1Image.gatsbyImageData} alt="" />
              </a>
            ) : null}
            {node.logo2Image ? (
              <a
                href={node.logo2Link}
                target="_blank"
                rel="noreferrer noopener"
              >
                <GatsbyImage image={node.logo2Image.gatsbyImageData} alt="" />
              </a>
            ) : null}
          </div>
        </div>
      </div>
      <div className="tracking-tight text-gray-800 bg-gray-200">
        <div className="container py-12 lg:pt-24 lg:pb-20">
          <div className="pb-6 border-b border-gray-400 lg:flex items-start">
            <div className="w-full lg:w-1/4">
              {disableLogoLinks ? (
                <>{logo}</>
              ) : (
                <Link url={getPath('/')} className="inline-block">
                  {logo}
                </Link>
              )}
              {/* Country selector disabled */}
              {/* <Disclosure as="div" className="my-5">
                {({ open }) => (
                  <>
                    {['LEAD_SE', 'LEAD_NO'].includes(node.country) ? null : (
                      <>
                        <Disclosure.Button className="flex items-center pr-2">
                          {node.country === 'FI' ? (
                            <span>
                              &#127467;&#127470;
                              <span className="pl-4 tracking-tight">
                                {t('Blok Finland')}
                              </span>
                            </span>
                          ) : null}{' '}
                          {node.country === 'SE' ? (
                            <span>
                              &#127480;&#127466;
                              <span className="pl-4 tracking-tight">
                                {t('Blok Sweden')}
                              </span>
                            </span>
                          ) : null}
                          {node.country === 'NO' ? (
                            <span>
                              &#127475;&#127476;
                              <span className="pl-4 tracking-tight">
                                {t('Blok Norway')}
                              </span>
                            </span>
                          ) : null}
                          <FaChevronDown
                            className={cx(
                              open ? 'rotate-180' : '',
                              'w-3 h-3 ml-2 transition duration-100 transform'
                            )}
                          />
                        </Disclosure.Button>
                        <Transition
                          show={open}
                          enter="transition duration-100 ease-linear"
                          enterFrom="transform opacity-0"
                          enterTo="transform opacity-100"
                          leave="transition duration-100 ease-linear"
                          leaveFrom="transform opacity-100"
                          leaveTo="transform opacity-0"
                        >
                          <Disclosure.Panel as="ul" className="pt-2">
                            <li>
                              {node.country === 'FI' ? (
                                <a className="py-1" href="https://blok.se/">
                                  &#127480;&#127466;
                                  <span className="pl-4">
                                    {t('Blok Sweden')}
                                  </span>
                                </a>
                              ) : null}
                              {node.country === 'SE' ? (
                                <a className="py-1" href="https://blok.ai/">
                                  &#127467;&#127470;
                                  <span className="pl-4">
                                    {t('Blok Finland')}
                                  </span>
                                </a>
                              ) : null}
                            </li>
                          </Disclosure.Panel>
                        </Transition>
                      </>
                    )}
                  </>
                )}
              </Disclosure> */}
              <ul className="space-y-2 mt-4">
                {node.copyright ? <li>{node.copyright}</li> : null}
                {node.streetAddress ? <li>{node.streetAddress}</li> : null}
                {node.streetAddress2 ? <li>{node.streetAddress2}</li> : null}
                {node.locality ? <li className="!mb-6">{node.locality}</li> : null}
                {node.email ? (
                  <li>
                    <a href={`mailto:${node.email}`}>{node.email}</a>
                  </li>
                ) : null}
                {node.phone ? (
                  <li>
                    <a href={`tel:${node.phone}`}>{node.phone}</a>
                  </li>
                ) : null}
                {node.vatId ? (
                  <li>
                    {t('Business ID')}: {node.vatId}
                  </li>
                ) : null}
              </ul>
            </div>
            <ul className="flex flex-wrap w-full mt-10 lg:mt-0 lg:w-3/4">
              {node.internalLinks
                ? node.internalLinks
                    // Remove links disabled in current language
                    .filter(
                      (link) => !link.disabledLanguages?.includes(currentLocale)
                    )
                    .map(({ id, itemLink, itemLabel }) => (
                      <li key={id} className="w-full mb-6 sm:w-1/2 md:w-1/3">
                        <Link
                          className="hover:text-pink-500"
                          url={itemLink}
                          target={linkTarget}
                        >
                          {itemLabel}
                        </Link>
                      </li>
                    ))
                : null}
            </ul>
          </div>
          <div className="pt-12 lg:flex">
            <span>
              &copy; {node.copyright} {new Date().getFullYear()}
            </span>
            <ul className="mt-4 space-y-4 lg:mt-0 lg:space-y-0 lg:flex lg:custom-list-disc">
              {node.termsOfService ? (
                <li>
                  <Link
                    url={node.termsOfService}
                    target={linkTarget}
                    className="hover:text-pink-500"
                  >
                    {node.termsOfServiceTitle
                      ? node.termsOfServiceTitle
                      : t('Terms of Service')}
                  </Link>
                </li>
              ) : null}
              {node.registry ? (
                <li>
                  <Link
                    url={node.registry}
                    target={linkTarget}
                    className="hover:text-pink-500"
                  >
                    {t('Data Protection Notice')}
                  </Link>
                </li>
              ) : null}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
